import { LitElement, classMap, css, html, nothing } from '@lit'
import { formatMoney, checkIsTouchDevice } from '@nova/utils'

const quickShopState = {
  initial: 'SHOW_OPTION_SELECTOR',
  start: 'SHOW_OPTION_STARTED',
  doing: 'SHOW_OPTION_DOING',
  submit: 'SHOW_ADD_TO_CART',
  loading: 'SHOW_LOADING',
  confirm: 'SHOW_CONFIRMATION',
  error: 'ERROR',
}

class NovaProductCardQuickShop extends LitElement {
  static properties = {
    productOptions: {
      type: Array,
      attribute: 'product-options',
    },
    productAvailable: {
      type: String,
      attribute: 'product-available',
    },
    variants: { type: Array },
    status: { type: String },
    activeVariant: {
      type: String,
      state: true,
    },
    url: {
      type: String,
    },
    available: {
      state: true,
    },
    activeOption: {
      type: Object,
      state: true,
    },
  }

  constructor() {
    super()
    this.activeVariant = ''
    this.available = true
  }

  connectedCallback() {
    super.connectedCallback()

    this.available = this.productAvailable === 'true'
    this.activeOption = null
    this.availableOptions = this.productOptions?.filter((option) => option.values.length > 1)
    if (this.availableOptions.length) {
      this.activeOption = this.availableOptions[0]
    }
  }

  get isTouchDevice() {
    return checkIsTouchDevice()
  }

  getVariantCompareAtPrice(variant) {
    if (!variant.compare_at_price) return false

    if (variant.compare_at_price > variant.price) return formatMoney(variant.compare_at_price)

    return false
  }

  findVariantsByOption(option) {
    return this.variants.filter((variant) => variant.options.includes(option))
  }

  findVariantByMultipleOptions(value) {
    return this.variants.find(
      (variant) =>
        variant.option1 === this.firstOptionValue && variant.option2 === value,
    )
  }

  setActiveVariant(variant) {
    this.activeVariant = variant
  }

  handleOptionClick(event) {
    const { option } = event.target.dataset
    this.firstOptionValue = option

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        bubbles: true,
        composed: true,
        detail: {},
      }),
    )
  }

  handleAddToBagClick(event) {
    let activeVariant = this.activeVariant
    if (!this.activeVariant) {
      const { variantId } = event.target.dataset
      activeVariant = this.variants.find(
        (variant) => variant.id === Number(variantId),
      )
      this.setActiveVariant(activeVariant)
    }

    this.firstOptionValue = ''

    this.dispatchEvent(
      new CustomEvent('add-to-bag', {
        bubbles: true,
        composed: true,
        detail: {
          variant: activeVariant,
        },
      }),
    )
  }

  startSelectOptions(event) {
    this.dispatchEvent(
      new CustomEvent('option-started', {
        bubbles: true,
        composed: true,
        detail: {},
      }),
    )
  }

  closeQuickShop() {
    this.dispatchEvent(
      new CustomEvent('close-quick-shop', {
        bubbles: true,
        composed: true,
        detail: {},
      }),
    )
  }

  handleNextOptionClick(event) {
    const { variantId } = event.target.dataset
    const activeVariant = this.variants.find(
      (variant) => variant.id === Number(variantId),
    )
    this.setActiveVariant(activeVariant)

    window.setTimeout(() => this.handleAddToBagClick(event), 50)
  }

  _renderOptions() {
    return html`
      <div 
        class='quick-shop__option--overlay'
        @click=${this.closeQuickShop}
        part='overlay'>
      </div>
      <div class="quick-shop__option--container" part="option-list">
        <div class="quick-shop__option--wrapper">
          <h3 class="quick-shop__option--label">Select ${this.activeOption.name}</h3>
          <div class="quick-shop__option--list">
            ${this.activeOption?.values?.map((value) => {
              const variants = this.findVariantsByOption(value)
              let variant = {
                id: -1,
                available: false,
              }
              if (variants.length === 1) {
                variant = variants[0]
              } else {
                const availableVariants = variants.filter(
                  (item) => item.available,
                )
                if (availableVariants.length > 0) {
                  variant = availableVariants[0]
                }
              }

              return html`
                <sl-button
                  data-variant-id=${variant.id}
                  data-option=${value}
                  aria-label="option: ${value}"
                  variant="primary"
                  size="medium"
                  ?disabled=${variant.available === false}
                  @click=${
                    variants.length > 1
                      ? this.handleOptionClick
                      : this.handleAddToBagClick
                  }
                  class="${variants.length === 1 ? 'js-option-with-price': ''}"
                >
                  ${value}
                  ${variants.length === 1 ? html `
                      <nova-product-card-price
                        class="product-card__price"
                        .price=${formatMoney(variant.price)}
                        .compareAtPrice=${this.getVariantCompareAtPrice(variant)}>
                      </nova-product-card-price>
                    `
                    : nothing
                  }
                </sl-button>
              `
            })}
          </div>
        </div>
      </div>
    `
  }

  _renderNextOptions() {
    if (typeof this.firstOptionValue === 'undefined') return nothing
    const nextOption = this.availableOptions[1]
    return html`
      <div 
        class='quick-shop__option--overlay'
        @click=${this.closeQuickShop}
        part='overlay'>
      </div>
      <div class="quick-shop__option--container" part="option-list">
        <div class="quick-shop__option--wrapper">
          <h3 class="quick-shop__option--label">Select ${nextOption.name}</h3>
          <div class="quick-shop__option--list">
            ${nextOption?.values?.map((value) => {
              const variant = this.findVariantByMultipleOptions(value)
              return html`
                <sl-button
                  data-variant-id=${variant.id}
                  data-option=${value}
                  aria-label="option: ${value}"
                  variant="primary"
                  size="medium"
                  ?disabled=${variant.available === false}
                  @click=${this.handleNextOptionClick}
                  class="js-option-with-price"
                >
                  ${value}
                  <nova-product-card-price
                    class="product-card__price"
                    .price=${formatMoney(variant.price)}
                    .compareAtPrice=${this.getVariantCompareAtPrice(variant)}>
                  </nova-product-card-price>
                </sl-button>
              `
            })}
          </div>
        </div>
      </div>
    `
  }

  render() {
    // if (this.isTouchDevice) return nothing

    if (!this.available) {
      return html`
        <div class="quick-shop__option_selector" part="base">         
          <a 
            class='btn btn--secondary'
            href=${this.url}
          >
            ${window.langMessages['products.product.notify_me']}
          </a>
        </div>
      `
    }

    if (this.status === quickShopState.confirm) {
      return html`
        <div class='quick-shop__option_selector' part='base'>
          <button class='btn btn--secondary'>
            ${window.langMessages['products.product.added']}
          </button>
        </div>
      `
    }

    if (this.availableOptions?.length === 0) {
      return html`
        <div class="quick-shop__option_selector" part="base">
          <sl-button
            variant="primary:ink"
            data-variant-id=${this.variants[0].id}
            ?loading=${this.status === quickShopState.loading}
            @click=${this.handleAddToBagClick}>
            ${window.langMessages['products.product.add_to_bag']}
          </sl-button>
        </div>
      `
    }

    if (this.availableOptions?.length === 1) {
      return html`
        <div class="quick-shop__option_selector ${classMap({
          'hidden--xl':
            this.status === quickShopState.start ||
            this.status === quickShopState.doing,
        })}" part="base">
          <sl-button
            variant="primary"
            @click=${this.startSelectOptions}>
            Select ${this.activeOption.name}
          </sl-button>
        </div>
        ${this.status === quickShopState.start ? this._renderOptions() : ''}
      `
    }

    if (this.availableOptions?.length > 1) {
      const optionNames = this.availableOptions
        .map((item) => item.name)
        .join(' & ')
      return html`
        <div class="quick-shop__option_selector ${classMap({
          'hidden--xl':
            this.status === quickShopState.start ||
            this.status === quickShopState.doing,
        })}" part="base">
          <sl-button
            variant="primary"
            @click=${this.startSelectOptions}>
            Select ${optionNames}
          </sl-button>
        </div>
        ${this.status === quickShopState.start ? this._renderOptions() : ''}
        ${this.status === quickShopState.doing ? this._renderNextOptions() : ''}
      `
    }
  }

  static styles = css`
    @media screen and (min-width: 1200px) {
      .hidden--xl {
        display: none !important;
      }
    }
    .quick-shop__option_selector {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .quick-shop__option_selector sl-button::part(base) {
      white-space: normal;
    }
    .quick-shop__option_selector sl-button::part(label) {
      padding: var(--nova-space-2xs) 0;
    }
    .quick-shop__option--container {
      margin-left: calc(0px - var(--nova-space-sm));
      margin-right: calc(0px - var(--nova-space-sm));
      margin-bottom: calc(0px - var(--nova-space-sm));
      width: calc(100% + 2 * var(--nova-space-sm));
      background-color: var(--nova-color-brand-tools-sec);
      max-height: 300px;
      overflow-y: auto;
    }
    .quick-shop__option--overlay {
      position: absolute;
      inset: 0;
      height: 100%;
      width: 100%;
      background: rgba(0, 0, 0, 0.40);
      border-radius: 8px;
      display: none;
    }
    @media screen and (max-width: 1199px) {
      .quick-shop__option--overlay {
        display: block;
      }
    }
    .quick-shop__option--wrapper {
      padding: var(--nova-space-sm);
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-sm);
    }
    .quick-shop__option--label {
      font: var(--nova-h8-font);
      line-height: calc(10 / 12 * 100%);
      text-transform: uppercase;
      margin: 0;
    }
    .quick-shop__option--list {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-xs);
    }
    sl-button::part(base),
    .btn {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: var(--nova-button-radius);
      border-width: 0;
      min-height: 40px;
      font-family: var(--nova-font-sans-bold);
      font-size: var(--nova-button-font-size);
      font-weight: 700;
      line-height: 100%;
      text-transform: uppercase;
      padding-inline: var(--nova-button-padding-left-right);
      transition: background-color 0.2s, color 0.2s, border-color 0.2s;
      text-decoration: none;
    }
    .btn--gap {
      gap: var(--nova-space-xs);
    }
    .btn--primary {
      background-color: var(--nova-color-brand-reverse);
      color: var(--nova-color-brand-charcoal);
    }
    .btn--primary:hover {
      background-color: var(--nova-color-brand-charcoal);
      color: var(--nova-color-brand-reverse);
    }
    .btn--secondary {
      background-color: var(--nova-color-brand-charcoal);
      color: var(--nova-color-brand-reverse);
    }
    sl-button[variant='primary:ink'],
    sl-button[variant='primary'] {
      width: 100%;
    }
    sl-button[variant='primary:ink']::part(base) {
      background-color: var(--nova-color-brand-charcoal);
      border: none;
      color: var(--nova-color-brand-reverse);
    }
    sl-button[variant='primary:ink']::part(base):hover {
      background-color: var(--nova-color-brand-charcoal);
      color: var(--nova-color-brand-reverse);
    }
    sl-button[variant='primary']::part(base) {
      background-color: var(--nova-color-brand-reverse);
      border: none;
      color: var(--nova-color-brand-charcoal);
    }
    sl-button[variant='primary']::part(base):hover {
      background-color: var(--nova-color-brand-charcoal);
      color: var(--nova-color-brand-reverse);
    }
    sl-button[disabled]::part(base) {
      background-color: var(--nova-color-grey-300);
      color: var(--nova-color-brand-reverse);
    }
    sl-button::part(label) {
      display: flex;
      justify-content: center;
      gap: var(--nova-space-xs);
      padding: var(--nova-space-xs) 0;
      white-space: normal;
    }
    nova-product-card-price {
      display: flex;
      align-items: center;
    }

    @media screen and (max-width: 1199px) {
      .quick-shop__option_selector sl-button[variant='primary']::part(base) {
        background-color: transparent;
        color: var(--nova-color-brand-charcoal);
        border: 1px solid var(--nova-color-brand-charcoal);
      }
    }
  `
}

customElements.define('nova-product-card-quick-shop', NovaProductCardQuickShop)
